/*  src/pages/SignUpPage.tsx  */
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { supabase } from '../supabaseClient.ts';
import { setRememberSession } from '../utils/authStorage.ts';
import { provisionStudentAccount } from '../utils/provisionStudent.ts';
import AuthCard from '../components/AuthCard.tsx';
import CurriculumFields from '../components/CurriculumFields.tsx';
import GoogleAuthButton from '../components/GoogleAuthButton.tsx';

const SignUpPage: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [yearId, setYearId] = useState('');
  const [subjectIds, setSubjectIds] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);

  const navigate = useNavigate();

  const toggleSubject = (id: string) =>
    setSubjectIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );

  const handleYearChange = (nextYearId: string) => {
    setYearId(nextYearId);
    setSubjectIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!yearId) {
      setError('Please choose a year level.');
      return;
    }

    setLoading(true);
    setRememberSession(true);

    const { data, error: signUpErr } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`,
        // The student record can't be created yet — email confirmation means
        // there's no session. Stash the details for provisionStudentAccount.
        data: {
          signup_role: 'student',
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          full_name: `${firstName.trim()} ${lastName.trim()}`,
          display_name: `${firstName.trim()} ${lastName.trim()}`,
          signup_year_id: yearId || null,
          signup_subject_ids: subjectIds,
        },
      },
    });

    if (signUpErr) {
      setError(signUpErr.message);
      setLoading(false);
      return;
    }

    // Supabase hides whether an email is taken by returning a user with no
    // identities rather than an error.
    if (data.user && (data.user.identities?.length ?? 0) === 0) {
      setError('An account with that email already exists. Try logging in instead.');
      setLoading(false);
      return;
    }

    // With email confirmation on there's no session yet; if it's off we can go
    // straight in.
    if (!data.session) {
      setConfirmSent(true);
      setLoading(false);
      return;
    }

    if (data.user) {
      try {
        await provisionStudentAccount(data.user);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not finish creating your account.');
        setLoading(false);
        return;
      }
    }

    setLoading(false);
    navigate('/practice');
  };

  if (confirmSent) {
    return (
      <AuthCard title="Check your email">
        <Typography variant="body2" color="text.secondary" textAlign="center">
          We sent a confirmation link to <strong>{email}</strong>. Open it to finish
          setting up your account.
        </Typography>
        <Button component={RouterLink} to="/practice" variant="outlined">
          Back to practice
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Create your account" onSubmit={handleSubmit} maxWidth={480}>
      <GoogleAuthButton disabled={loading} remember onError={setError} />

      <Divider>or</Divider>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoComplete="given-name"
          fullWidth
          required
          autoFocus
        />
        <TextField
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          autoComplete="family-name"
          fullWidth
          required
        />
      </Stack>

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        fullWidth
        required
      />

      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        helperText="At least 6 characters"
        fullWidth
        required
      />

      <CurriculumFields
        yearId={yearId}
        subjectIds={subjectIds}
        onYearChange={handleYearChange}
        onToggleSubject={toggleSubject}
        onError={setError}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
      >
        {loading ? 'Creating account…' : 'Create account'}
      </Button>

      <Button component={RouterLink} to="/login">
        Already have an account? Log in
      </Button>
    </AuthCard>
  );
};

export default SignUpPage;
